'use strict';

/**
 * Single mock project panel for the admin showcase — same tracker surface as
 * `/home/project/<projectCode>/home` (PipelineProgressSummaryView), fed from fixtures.
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Gray } from 'metabux/theme/colors';
import { PipelineProgressSummaryView } from './PipelineProgressSummary';
import {
	buildShowcasePipelineProgress,
	buildSyntheticShowcaseProgress,
} from './buildShowcasePipelineProgress';
import { showcaseProjectCode } from './pipelineStatusShowcaseCatalog';

/**
 * @param {{ scenario: import('./pipelineStatusShowcaseCatalog').ShowcaseScenario }} props
 */
export function PipelineStatusShowcaseCard({ scenario }) {
	const projectCode = showcaseProjectCode(scenario);

	const progress = useMemo(() => {
		if (scenario.synthetic === 'loading' || scenario.synthetic === 'error') {
			return buildSyntheticShowcaseProgress(scenario.synthetic);
		}
		try {
			const bundle = buildShowcasePipelineProgress(scenario.mockScenarioKey);
			return {
				...bundle,
				isLoading: false,
				error: undefined,
			};
		} catch (err) {
			return {
				...buildSyntheticShowcaseProgress('error'),
				error: err,
			};
		}
	}, [scenario]);

	return (
		<Box
			component="section"
			data-testid={`pipeline-showcase-card-${scenario.id}`}
			data-showcase-project-code={projectCode}
			sx={{ width: '100%', minWidth: 0 }}
		>
			{/* Mirrors Project.jsx projectHeader + PipelineProgressSummary on /home */}
			<Box
				className="projectHeader"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					mb: 1.5,
					maxWidth: 1290,
				}}
			>
				<Box>
					<Typography
						className="title"
						component="div"
						sx={{ color: Gray[800], fontSize: '21px', fontWeight: 500 }}
						data-testid="pipeline-showcase-project-title"
					>
						{scenario.label}
					</Typography>
					<Typography
						variant="body2"
						sx={{ color: Gray[600], mt: 0.5, fontFamily: 'monospace' }}
						data-testid="pipeline-showcase-project-code"
					>
						/home/project/{projectCode}/home
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
						{scenario.description}
					</Typography>
				</Box>
			</Box>

			<PipelineProgressSummaryView
				kind={progress.kind}
				model={progress.model}
				coreModel={progress.coreModel}
				discoveryModel={progress.discoveryModel}
				kitsModel={progress.kitsModel}
				stages={progress.stages ?? []}
				facts={progress.facts}
				definition={progress.definition}
				isLoading={progress.isLoading}
				error={progress.error}
			/>
		</Box>
	);
}
